import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandingProvider } from './context/BrandingContext';
import { Toaster } from 'sonner';
import Layout from './components/Layout';
import TutorialOverlay from './components/TutorialOverlay';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProductTypesPage from './pages/ProductTypesPage';
import ItemsPage from './pages/ItemsPage';
import ItemDetailPage from './pages/ItemDetailPage';
import ItemDashboardPage from './pages/ItemDashboardPage';
import OpenOrdersPage from './pages/OpenOrdersPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import PurchaseOrderDetailPage from './pages/PurchaseOrderDetailPage';
import DataIntegrityPage from './pages/DataIntegrityPage';
import FaceplatesPage from './pages/FaceplatesPage';
import SerialRunPage from './pages/SerialRunPage';
import SerialRecordPage from './pages/SerialRecordPage';
import ScanSerialPage from './pages/ScanSerialPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import BatchLotRecallPage from './pages/BatchLotRecallPage';
import VendorScorecardPage from './pages/VendorScorecardPage';
import LabelLibraryPage from './pages/LabelLibraryPage';
import VendorsPage from './pages/VendorsPage';
import VendorDetailPage from './pages/VendorDetailPage';
import RfxPage from './pages/RfxPage';
import DisposalTicketsPage from './pages/DisposalTicketsPage';
import PublicDisposalTicketPage from './pages/PublicDisposalTicketPage';
import PublicPoConfirmPage from './pages/PublicPoConfirmPage';
import AuditsPage from './pages/AuditsPage';
import WarrantyPage from './pages/WarrantyPage';
import ReworkTasksPage from './pages/ReworkTasksPage';
import ScorecardSchedulePage from './pages/ScorecardSchedulePage';
import LocationsPage from './pages/LocationsPage';
import LocationViewPage from './pages/LocationViewPage';
import LocationMapPage from './pages/LocationMapPage';
import WorkOrderDetailPage from './pages/WorkOrderDetailPage';
import BOMsPage from './pages/BOMsPage';
import BomDetailPage from './pages/BomDetailPage';
import InventoryVarianceReport from './pages/InventoryVarianceReport';
import CompetitorRepairsPage from './pages/CompetitorRepairsPage';
// iter71 — WorkOrdersPage.js was deleted; /work-orders renders ProductionQueuePage
import ProductionQueuePage from './pages/ProductionQueuePage';
import FinishedUnitsPage from './pages/FinishedUnitsPage';
import TransactionsPage from './pages/TransactionsPage';
import LabelsPage from './pages/LabelsPage';
import UsersPage from './pages/UsersPage';
import AIAssistantPage from './pages/AIAssistantPage';
import BugReportsPage from './pages/BugReportsPage';
import ReportsPage from './pages/ReportsPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import ShiftSchedulingPage from './pages/ShiftSchedulingPage';
import UnitGenealogyPage from './pages/UnitGenealogyPage';
import MobileOperatorPage from './pages/MobileOperatorPage';
import CustomDashboardPage from './pages/CustomDashboardPage';
import InboxPage from './pages/InboxPage';
import ItemCategoriesPage from './pages/ItemCategoriesPage';
import QCTemplatesPage from './pages/QCTemplatesPage';
import ShippingPage from './pages/ShippingPage';
import TvViewPage from './pages/TvViewPage';
import PublicItemPage from './pages/PublicItemPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import CustomerHistoryPage from './pages/CustomerHistoryPage';
import CustomersPage from './pages/CustomersPage';
import OrdersHomePage from './pages/orders/OrdersHomePage';
import OrdersPlaceholderPage from './pages/orders/OrdersPlaceholderPage';
import SalesOrdersPage from './pages/orders/SalesOrdersPage';
import SalesOrderDetailPage from './pages/orders/SalesOrderDetailPage';
import ProductionOrdersPage from './pages/orders/ProductionOrdersPage';
import ProductionOrderDetailPage from './pages/orders/ProductionOrderDetailPage';
import PackingSlipsPage from './pages/orders/PackingSlipsPage';
import PackingSlipDetailPage from './pages/orders/PackingSlipDetailPage';
import BillsOfLadingPage from './pages/orders/BillsOfLadingPage';
import BillOfLadingDetailPage from './pages/orders/BillOfLadingDetailPage';
import RepairOrdersPage from './pages/orders/RepairOrdersPage';
import RepairOrderDetailPage from './pages/orders/RepairOrderDetailPage';
import WorkOrdersPage from './pages/orders/WorkOrdersPage';
import WorkOrderV2DetailPage from './pages/orders/WorkOrderDetailPage';
import OrdersOverviewPage from './pages/orders/OrdersOverviewPage';
import CarriersPage from './pages/CarriersPage';
import ShippingAnalyticsPage from './pages/ShippingAnalyticsPage';
import RDProjectsPage from './pages/RDProjectsPage';
import RDProjectDetailPage from './pages/RDProjectDetailPage';
import RDMyTasksPage from './pages/RDMyTasksPage';
import RDTeamWorkloadPage from './pages/RDTeamWorkloadPage';
import ProductStructurePage from './pages/ProductStructurePage';
import RDApprovalsPage from './pages/RDApprovalsPage';
import StreamOverviewPage from './pages/StreamOverviewPage';
import './App.css';

const ProtectedRoute = ({ children, roles, bare = false }) => {
  const { user, loading, hasRole } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    try {
      const here = window.location.pathname + window.location.search;
      if (here && here !== '/login' && here !== '/') window.sessionStorage.setItem('post_login_redirect', here);
    } catch (_) { /* ignore */ }
    return <Navigate to="/login" replace />;
  }
  try {
    if (window.sessionStorage.getItem('post_login_redirect') === window.location.pathname + window.location.search) {
      window.sessionStorage.removeItem('post_login_redirect');
    }
  } catch (_) { /* ignore */ }
  
  if (roles && !hasRole(roles)) {
    return <Navigate to="/" replace />;
  }

  if (user.must_change_password && window.location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  
  if (bare || user.must_change_password) {
    return <>{children}</>;
  }
  return (
    <Layout>
      {children}
      <TutorialOverlay />
    </Layout>
  );
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="spinner" />
      </div>
    );
  }
  
  if (user) {
    let dest = '/';
    try { dest = window.sessionStorage.getItem('post_login_redirect') || '/'; } catch (_) { /* ignore */ }
    return <Navigate to={dest} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      {/* iter77 — public password recovery */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* iter77 — authenticated profile (change pw, edit birthday/name) */}
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      {/* Phase C — public no-login Item card */}
      <Route path="/public/items/:token" element={<PublicItemPage />} />
      {/* Phase E — public disposal ticket scan view */}
      <Route path="/public/disposal-tickets/:token" element={<PublicDisposalTicketPage />} />
      <Route path="/public/po-confirm/:token" element={<PublicPoConfirmPage />} />
      <Route path="/" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/product-types" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <ProductTypesPage />
        </ProtectedRoute>
      } />
      <Route path="/items" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing']}>
          <ItemsPage />
        </ProtectedRoute>
      } />
      <Route path="/items/dashboard" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing']}>
          <ItemDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/items/view/:id" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing', 'Operator']}>
          <ItemDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/purchasing/pos" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'Purchasing']}>
          <PurchaseOrdersPage />
        </ProtectedRoute>
      } />
      <Route path="/purchasing/pos/:id" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'Purchasing']}>
          <PurchaseOrderDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/faceplates" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Operator']}>
          <FaceplatesPage />
        </ProtectedRoute>
      } />
      <Route path="/faceplates/runs/:id" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Operator']}>
          <SerialRunPage />
        </ProtectedRoute>
      } />
      <Route path="/serials/:serial" element={
        <ProtectedRoute>
          <SerialRecordPage />
        </ProtectedRoute>
      } />
      <Route path="/change-password" element={
        <ProtectedRoute>
          <ChangePasswordPage />
        </ProtectedRoute>
      } />
      <Route path="/scan-serial" element={
        <ProtectedRoute>
          <ScanSerialPage />
        </ProtectedRoute>
      } />
      <Route path="/data-integrity" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'Purchasing', 'ProductionSupervisor']}>
          <DataIntegrityPage />
        </ProtectedRoute>
      } />
      <Route path="/open-orders" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing']}>
          <OpenOrdersPage />
        </ProtectedRoute>
      } />
      <Route path="/batch-lot-recall" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing']}>
          <BatchLotRecallPage />
        </ProtectedRoute>
      } />
      <Route path="/vendors/scorecard" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'Purchasing']}>
          <VendorScorecardPage />
        </ProtectedRoute>
      } />
      <Route path="/labels/library" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing', 'Operator']}>
          <LabelLibraryPage />
        </ProtectedRoute>
      } />
      <Route path="/item-categories" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing']}>
          <ItemCategoriesPage />
        </ProtectedRoute>
      } />
      <Route path="/vendors" element={
        <ProtectedRoute roles={['Admin', 'Purchasing']}>
          <VendorsPage />
        </ProtectedRoute>
      } />
      <Route path="/vendors/view/:id" element={
        <ProtectedRoute roles={['Admin', 'Purchasing', 'InventoryManager']}>
          <VendorDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/rfx" element={
        <ProtectedRoute roles={['Admin', 'Purchasing', 'InventoryManager']}>
          <RfxPage />
        </ProtectedRoute>
      } />
      <Route path="/disposal-tickets" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Operator']}>
          <DisposalTicketsPage />
        </ProtectedRoute>
      } />
      <Route path="/audits" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager']}>
          <AuditsPage />
        </ProtectedRoute>
      } />
      <Route path="/warranty" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing']}>
          <WarrantyPage />
        </ProtectedRoute>
      } />
      <Route path="/rework" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor', 'Operator']}>
          <ReworkTasksPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/scorecard-schedule" element={
        <ProtectedRoute roles={['Admin', 'Purchasing', 'InventoryManager']}>
          <ScorecardSchedulePage />
        </ProtectedRoute>
      } />
      <Route path="/locations" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager']}>
          <LocationsPage />
        </ProtectedRoute>
      } />
      <Route path="/locations/view/:id" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager']}>
          <LocationViewPage />
        </ProtectedRoute>
      } />
      <Route path="/locations/map" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager']}>
          <LocationMapPage />
        </ProtectedRoute>
      } />
      <Route path="/work-orders/:id" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor', 'Operator']}>
          <WorkOrderDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/boms" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor']}>
          <BOMsPage />
        </ProtectedRoute>
      } />
      <Route path="/boms/view/:id" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor', 'Operator']}>
          <BomDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/reports/competitor-repairs" element={
        <ProtectedRoute>
          <CompetitorRepairsPage />
        </ProtectedRoute>
      } />
      <Route path="/reports/inventory-variance" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <InventoryVarianceReport />
        </ProtectedRoute>
      } />
      <Route path="/work-orders" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor', 'Operator']}>
          <ProductionQueuePage />
        </ProtectedRoute>
      } />
      <Route path="/qc-templates" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor']}>
          <QCTemplatesPage />
        </ProtectedRoute>
      } />
      <Route path="/shipping" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor', 'Operator']}>
          <ShippingPage />
        </ProtectedRoute>
      } />
      <Route path="/tv" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor', 'Operator', 'InventoryManager', 'Purchasing']} bare>
          <TvViewPage />
        </ProtectedRoute>
      } />
      <Route path="/finished-units" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor', 'Operator']}>
          <FinishedUnitsPage />
        </ProtectedRoute>
      } />
      <Route path="/transactions" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager']}>
          <TransactionsPage />
        </ProtectedRoute>
      } />
      <Route path="/labels" element={
        <ProtectedRoute>
          <LabelsPage />
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute roles={['Admin']}>
          <UsersPage />
        </ProtectedRoute>
      } />
      <Route path="/ai-assistant" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing']}>
          <AIAssistantPage />
        </ProtectedRoute>
      } />
      <Route path="/bugs" element={
        <ProtectedRoute>
          <BugReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <ReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute roles={['Admin']}>
          <AdminSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/shift-scheduling" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor', 'Operator']}>
          <ShiftSchedulingPage />
        </ProtectedRoute>
      } />
      <Route path="/unit-genealogy" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor', 'Operator']}>
          <UnitGenealogyPage />
        </ProtectedRoute>
      } />
      <Route path="/mobile" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor', 'Operator']}>
          <MobileOperatorPage />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <CustomDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/inbox" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing', 'Operator']}>
          <InboxPage />
        </ProtectedRoute>
      } />
      {/* iter83 — Orders & Shipping */}
      <Route path="/customers" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing']}>
          <CustomersPage />
        </ProtectedRoute>
      } />
      <Route path="/customers/:id/history" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing']}>
          <CustomerHistoryPage />
        </ProtectedRoute>
      } />
      <Route path="/carriers" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager']}>
          <CarriersPage />
        </ProtectedRoute>
      } />
      <Route path="/shipping-analytics" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <ShippingAnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/orders" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing']}>
          <OrdersHomePage />
        </ProtectedRoute>
      } />
      <Route path="/orders/sales" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing']}>
          <SalesOrdersPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/sales/new" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing']}>
          <SalesOrderDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/sales/:id" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing']}>
          <SalesOrderDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/production" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor']}>
          <ProductionOrdersPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/production/new" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor']}>
          <ProductionOrderDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/production/:id" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor']}>
          <ProductionOrderDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/packing" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <PackingSlipsPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/packing/new" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <PackingSlipDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/packing/:id" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <PackingSlipDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/bol" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <BillsOfLadingPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/bol/new" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <BillOfLadingDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/bol/:id" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <BillOfLadingDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/repairs" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <RepairOrdersPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/repairs/new" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <RepairOrderDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/repairs/:id" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <RepairOrderDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/work" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <WorkOrdersPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/work/new" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <WorkOrderV2DetailPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/work/:id" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <WorkOrderV2DetailPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/overview" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <OrdersOverviewPage />
        </ProtectedRoute>
      } />
      <Route path="/assemblies" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Engineer', 'Operator']}>
          <StreamOverviewPage level="assembly" />
        </ProtectedRoute>
      } />
      <Route path="/assemblies/catalog" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <ProductTypesPage key="asm-cat" level="assembly" />
        </ProtectedRoute>
      } />
      <Route path="/assemblies/boms" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor']}>
          <BOMsPage key="asm-boms" level="assembly" />
        </ProtectedRoute>
      } />
      <Route path="/assemblies/builds" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor', 'Operator']}>
          <ProductionQueuePage key="asm-builds" level="assembly" />
        </ProtectedRoute>
      } />
      <Route path="/products" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Engineer', 'Operator']}>
          <StreamOverviewPage level="product" />
        </ProtectedRoute>
      } />
      <Route path="/products/catalog" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <ProductTypesPage key="prod-cat" level="product" />
        </ProtectedRoute>
      } />
      <Route path="/products/families" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor']}>
          <ProductTypesPage key="prod-fam" level="product" initialTab="families" />
        </ProtectedRoute>
      } />
      <Route path="/products/boms" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor']}>
          <BOMsPage key="prod-boms" level="product" />
        </ProtectedRoute>
      } />
      <Route path="/products/builds" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor', 'Operator']}>
          <ProductionQueuePage key="prod-builds" level="product" />
        </ProtectedRoute>
      } />
      <Route path="/structure" element={
        <ProtectedRoute roles={['Admin', 'InventoryManager', 'ProductionSupervisor', 'Engineer']}>
          <ProductStructurePage />
        </ProtectedRoute>
      } />
      <Route path="/rd" element={
        <ProtectedRoute roles={['Admin', 'Engineer', 'ProductionSupervisor', 'InventoryManager']}>
          <RDProjectsPage />
        </ProtectedRoute>
      } />
      <Route path="/rd/my-tasks" element={
        <ProtectedRoute roles={['Admin', 'Engineer', 'ProductionSupervisor', 'InventoryManager']}>
          <RDMyTasksPage />
        </ProtectedRoute>
      } />
      <Route path="/rd/approvals" element={
        <ProtectedRoute roles={['Admin', 'Engineer', 'ProductionSupervisor', 'InventoryManager']}>
          <RDApprovalsPage />
        </ProtectedRoute>
      } />
      <Route path="/rd/team-workload" element={
        <ProtectedRoute roles={['Admin', 'ProductionSupervisor', 'InventoryManager']}>
          <RDTeamWorkloadPage />
        </ProtectedRoute>
      } />
      <Route path="/rd/:id" element={
        <ProtectedRoute roles={['Admin', 'Engineer', 'ProductionSupervisor', 'InventoryManager']}>
          <RDProjectDetailPage />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <BrandingProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrandingProvider>
    </BrowserRouter>
  );
}

export default App;
